import { browser_session_storage } from './browser-storage'

const DEFAULT_PUBLIC_ORIGIN = 'https://nabomba.com'
const DEVELOPMENT_HOSTS = new Set(['dev.nabomba.com', 'localhost', '127.0.0.1'])
const DEVELOPMENT_CLEANUP_KEY = 'fidelidade_dev_sw_cleanup_v1'

type CapacitorSplashScreen = {
  hide?: (options?: Record<string, unknown>) => Promise<void> | void
}

type CapacitorBridge = {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
  Plugins?: {
    SplashScreen?: CapacitorSplashScreen
  }
}

function text(value: unknown): string {
  return (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' ? String(value) : '').trim()
}

function clean_origin(value: unknown): string {
  const raw = text(value)
  if (!raw) return ''
  try {
    const url = new URL(raw)
    return url.origin
  } catch {
    return raw.replace(/\/+$/, '')
  }
}

function capacitor_bridge(): CapacitorBridge | null {
  return (globalThis as { Capacitor?: CapacitorBridge }).Capacitor || null
}

function is_internal_native_origin(origin: string): boolean {
  return /^capacitor:\/\//i.test(origin)
    || /^ionic:\/\//i.test(origin)
    || /^https?:\/\/localhost(?::\d+)?$/i.test(origin)
}

function is_development_browser(): boolean {
  if (typeof window === 'undefined' || is_native_app()) return false
  return import.meta.env.DEV || DEVELOPMENT_HOSTS.has(window.location.hostname)
}

export function is_native_app(): boolean {
  const bridge = capacitor_bridge()
  if (typeof bridge?.isNativePlatform === 'function') return bridge.isNativePlatform()
  if (typeof window === 'undefined') return false
  return window.location.protocol === 'capacitor:' || window.location.protocol === 'ionic:'
}

export function public_app_origin(): string {
  const configured = clean_origin(import.meta.env.VITE_PUBLIC_APP_URL || import.meta.env.VITE_API_URL)
  if (configured) return configured
  if (typeof window === 'undefined') return DEFAULT_PUBLIC_ORIGIN
  const origin = clean_origin(window.location.origin)
  return origin && !is_internal_native_origin(origin) ? origin : DEFAULT_PUBLIC_ORIGIN
}

export function api_base_url(): string {
  const configured = clean_origin(import.meta.env.VITE_API_URL)
  if (configured) return configured
  return is_native_app() ? DEFAULT_PUBLIC_ORIGIN : ''
}

export function api_url(path: string): string {
  const raw = text(path)
  if (!raw) return api_base_url() || '/'
  if (/^https?:\/\//i.test(raw)) return raw
  const normalized = raw.startsWith('/') ? raw : `/${raw}`
  const base = api_base_url()
  return base ? `${base}${normalized}` : normalized
}

export function should_register_browser_service_worker(): boolean {
  return !is_development_browser()
    && !is_native_app()
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
}

// O worker novo espera a abertura terminar (public/sw.js). Depois da primeira
// tela montada, o app pede a troca ao worker que já espera e ao que terminar de
// instalar depois. Mesmo padrão do messageSkipWaiting do Workbox (PWA.md).
export function activate_service_worker_update(registration: ServiceWorkerRegistration, ready: Promise<void>): void {
  const skip_waiting = (worker: ServiceWorker | null | undefined) => { worker?.postMessage({ type: 'SKIP_WAITING' }) }
  const watch = (worker: ServiceWorker | null) => {
    worker?.addEventListener('statechange', () => {
      if (worker.state === 'installed') skip_waiting(worker)
    })
  }
  void ready.then(() => {
    skip_waiting(registration.waiting)
    watch(registration.installing)
    registration.addEventListener('updatefound', () => watch(registration.installing))
  })
}

export async function prepare_development_browser_runtime(): Promise<boolean> {
  if (!is_development_browser()) return true
  const was_controlled = typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && !!navigator.serviceWorker.controller

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map(registration => registration.unregister()))
  }
  if (typeof caches !== 'undefined') {
    const cache_names = await caches.keys()
    await Promise.all(cache_names.map(cache_name => caches.delete(cache_name)))
  }

  if (was_controlled && !browser_session_storage.getItem(DEVELOPMENT_CLEANUP_KEY)) {
    browser_session_storage.setItem(DEVELOPMENT_CLEANUP_KEY, '1')
    window.location.reload()
    return false
  }
  browser_session_storage.removeItem(DEVELOPMENT_CLEANUP_KEY)
  return true
}

export async function hide_native_splash(): Promise<void> {
  if (!is_native_app()) return
  const splash = capacitor_bridge()?.Plugins?.SplashScreen
  if (typeof splash?.hide !== 'function') return
  try {
    await splash.hide({ fadeOutDuration: 180 })
  } catch (err) {
    console.warn('[native] falha ao esconder splash:', err)
  }
}
